import ActionMenu from "../Menus/ActionMenu";
import ServiceCategoryTooltip from "../ServiceCategoryTooltip";
import { CheckBadgeIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { resolvePrimaryLocationDisplay } from "../../utils/myVendorsMapper";
import { Tooltip } from "@mui/material";

import { vendorTypeColors } from "../../utils/constants";
import {
  filterAndSortKnownServiceNames,
  resolveServiceBadgePresentation,
} from "../../utils/serviceCategoryBadgeDisplay";

// Reusable provider name: tooltip only when text overflows 3 lines (DOM-based)
const ProviderName = ({ name, className = "" }) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const overflowPx = el.scrollHeight - el.clientHeight;
    if (overflowPx <= 0) {
      setIsOverflowing(false);
      return;
    }
    // Only consider "cut off" when at least ~half a line is clipped (avoids subpixel
    // and layout false positives when the name fits, e.g. "Alpha Bioresearch")
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || el.clientHeight;
    const trimmed = overflowPx >= lineHeight * 0.5;
    setIsOverflowing(trimmed);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure after layout so scrollHeight/clientHeight are accurate
    const raf = requestAnimationFrame(() => {
      checkOverflow();
    });
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [name, checkOverflow]);

  const span = (
    <span
      ref={ref}
      className={`line-clamp-3 break-words ${className}`.trim()}
    >
      {name}
    </span>
  );

  if (isOverflowing) {
    return (
      <Tooltip
        title={
          <span style={{ color: "black" }} className="flex items-center rounded-md text-xs">
            {name}
          </span>
        }
        arrow
        placement="bottom"
        PopperProps={{
          style: { backgroundColor: "white", color: "black" },
        }}
        classes={{ tooltip: "custom-tooltip" }}
      >
        {span}
      </Tooltip>
    );
  }
  return span;
};

// Vendor name mapping for logo files
const getLogoFileName = (vendorName) => {
  const logoMapping = {
    Veeva: "Veeva Vault",
    Icon: "IQVIA", // Fallback since Icon logo doesn't exist
    Medpace: "PPD", // Fallback since Medpace logo doesn't exist
    Covance: "Labcorp", // Covance is now part of Labcorp
    "Charles River": "PPD", // Fallback since Charles River logo doesn't exist
    "WuXi AppTec": "IQVIA", // Fallback since WuXi AppTec logo doesn't exist
    Catalent: "PPD", // Fallback since Catalent logo doesn't exist
    Lonza: "IQVIA", // Fallback since Lonza logo doesn't exist
    "Oracle Health Sciences": "Oracle", // Use Oracle logo for Oracle Health Sciences
    // Add more mappings as needed
  };

  return logoMapping[vendorName] || vendorName;
};

// Normalize to a filename base: remove non-alphanumerics and whitespace, preserve casing
const normalizeLogoBase = (name) =>
  (name || "").toString().replace(/[^A-Za-z0-9]/g, "");

// Build ordered src candidates: providers folder first, then root images folder
const getLogoSrcCandidates = (vendorName) => {
  const base = "1med";
  return [
    // Prefer local assets first for speed and stability
    `../../assets/images/${base}.png`,
    `../../assets/images/${base}.svg`,
    `../../assets/images/${base}.png`,
    `../../assets/images/${base}.svg`,
  ];
};

// Generate GxP values for a provider based on their name
const generateGxPsForProvider = (providerName, services) => {
  if (!providerName || !services || services.length === 0) return [];

  const gxpOptions = [
    "GCP",
    "GCP",
    "GCP",
    "GCP",
    "GCP",
    "GCP",
    "GCP",
    "GMP",
    "GLP",
    "GVP",
    "GCLP",
    "GDP",
    "GAMP",
  ];

  // Use provider name hash to deterministically select 1-3 GxP values
  const hash = providerName
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const numGxPs = (hash % 3) + 1; // 1-3 GxP values

  const gxpSet = new Set();
  for (let i = 0; i < numGxPs; i++) {
    const index = (hash + i * 7) % gxpOptions.length;
    gxpSet.add(gxpOptions[index]);
  }

  return Array.from(gxpSet).sort();
};

function occurrences(provider, word) {
  const string = provider.documents || "";
  if (!string) {
    if (word === "RFI") return provider.rfi_count || 0;
    else if (word === "Audit") return provider.assessment_count || 0;
  }
  return string.split(word).length - 1;
}

export default function ProviderCardList({
  providers,
  selectedOptions,
  setSelectedOptions,
  serviceColors,
  hideRequestActions = false,
  canSelectVendors = false,
  canManageMyVendors = false,
  getMyVendorMembership = null,
  onAddToMyVendors = null,
  onRemoveFromMyVendors = null,
  onAddToHidden = null,
  onUnhide = null,
  onCreateList = null,
  onAddToList = null,
  isBookmarked = null,
  onToggleBookmark = null,
  isBookmarkPending = null,
  hideVqq = false,
}) {
  const navigate = useNavigate();

  // Validate and deduplicate providers to prevent rendering issues
  const validProviders = React.useMemo(() => {
    if (!Array.isArray(providers)) return [];

    // Filter out invalid providers and deduplicate by ID
    const seen = new Set();
    const valid = providers.filter((provider) => {
      if (!provider || !provider.id || !provider.name) {
        console.warn("Invalid provider detected:", provider);
        return false;
      }
      if (seen.has(provider.id)) {
        console.warn(
          `Duplicate provider ID detected in ProviderCardList: ${provider.id} (${provider.name})`
        );
        return false;
      }
      seen.add(provider.id);
      return true;
    });

    if (valid.length !== providers.length) {
      console.warn(
        `ProviderCardList: Filtered out ${
          providers.length - valid.length
        } invalid/duplicate providers`
      );
    }

    return valid;
  }, [providers]);

  // Helper to normalize provider name for robust comparison
  const normalizeName = (name) =>
    (name || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");

  // Get a set of normalized selected provider names
  const selectedNamesSet = new Set(
    (selectedOptions || []).map((p) => normalizeName(p.name))
  );

  // Handle selection toggle for a provider
  const handleToggleSelection = (provider, e) => {
    e.stopPropagation(); // Prevent card click
    const normName = normalizeName(provider.name);

    if (selectedNamesSet.has(normName)) {
      // Remove from selection
      setSelectedOptions(
        selectedOptions.filter((p) => normalizeName(p.name) !== normName)
      );
    } else {
      // Add to selection
      setSelectedOptions([...selectedOptions, provider]);
    }
  };

  const handleProviderClick = (provider) => {
    navigate(`/provider/${provider.id}/overview`);
    // Scroll to top when navigating to provider profile
    window.scrollTo(0, 0);
  };

  // Calculate how many placeholders are needed to fill the last row
  const remainder = validProviders.length % 3;
  const placeholders = remainder === 0 ? 0 : 3 - remainder;

  // Track which images failed to load (all candidates)
  const [brokenLogos, setBrokenLogos] = React.useState({});
  const handleAllImgFailed = (providerName) =>
    setBrokenLogos((prev) => ({ ...prev, [providerName]: true }));

  return (
    <div className="mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {validProviders?.map((provider, index) => (
          <ProviderCard
            key={provider.id || `${provider.name}-${index}`}
            provider={provider}
            onProviderClick={handleProviderClick}
            brokenLogos={brokenLogos}
            onImgError={handleAllImgFailed}
            isSelected={selectedNamesSet.has(normalizeName(provider.name))}
            onToggleSelection={(e) => handleToggleSelection(provider, e)}
            serviceColors={serviceColors}
            hideRequestActions={hideRequestActions}
            canSelectVendors={canSelectVendors}
            canManageMyVendors={canManageMyVendors}
            getMyVendorMembership={getMyVendorMembership}
            onAddToMyVendors={onAddToMyVendors}
            onRemoveFromMyVendors={onRemoveFromMyVendors}
            onAddToHidden={onAddToHidden}
            onUnhide={onUnhide}
            onCreateList={onCreateList}
            onAddToList={onAddToList}
            isBookmarked={isBookmarked}
            onToggleBookmark={onToggleBookmark}
            isBookmarkPending={isBookmarkPending}
            hideVqq={hideVqq}
          />
        ))}
        {/* Add empty placeholders to fill out the last row if needed */}
        {Array.from({ length: placeholders }, (_, index) => (
          <div key={`placeholder-${index}`} className="invisible">
            {/* Invisible placeholder to maintain grid layout */}
          </div>
        ))}
      </div>
    </div>
  );
}

// Individual Provider Card Component with Expandable Services (keeping original UI)
function ProviderCard({
  provider,
  onProviderClick,
  brokenLogos,
  onImgError,
  isSelected,
  onToggleSelection,
  serviceColors,
  hideRequestActions = false,
  canSelectVendors = false,
  canManageMyVendors = false,
  getMyVendorMembership = null,
  onAddToMyVendors = null,
  onRemoveFromMyVendors = null,
  onAddToHidden = null,
  onUnhide = null,
  onCreateList = null,
  onAddToList = null,
  isBookmarked = null,
  onToggleBookmark = null,
  isBookmarkPending = null,
  hideVqq = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const providerBookmarked = isBookmarked ? isBookmarked(provider.id) : false;
  const providerBookmarkPending = isBookmarkPending
    ? isBookmarkPending(provider.id)
    : false;
  const initialServices =
    provider.provider_capabilities?.map((p) => p.rfi_name) || [];
  let predefinedServices = filterAndSortKnownServiceNames(
    initialServices,
    serviceColors
  );

  // Get unique GxP values for this provider
  const uniqueGxPs = useMemo(() => {
    if (!provider) return [];
    return generateGxPsForProvider(provider.name, predefinedServices);
  }, [provider, predefinedServices]);

  // Dynamic Badge Width Measurement Function
  const measureBadgeWidths = (services) => {
    const measurer = document.createElement("div");
    measurer.style.position = "absolute";
    measurer.style.visibility = "hidden";
    measurer.style.whiteSpace = "nowrap";
    measurer.style.pointerEvents = "none";
    measurer.className = "flex flex-wrap gap-1";

    document.body.appendChild(measurer);

    const measurements = [];
    const moreTagMeasurements = {};

    try {
      // Measure each service badge
      services.forEach((service, index) => {
        const badge = document.createElement("span");
        badge.className =
          "inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10 mb-1 mr-2";
        badge.textContent = service.trim();

        measurer.appendChild(badge);
        const rect = badge.getBoundingClientRect();
        measurements.push({
          service,
          width: rect.width || 80,
          index,
        });
        measurer.removeChild(badge);
      });

      // Measure "+X more" badge with different counts
      for (let i = 1; i <= services.length; i++) {
        const moreBadge = document.createElement("span");
        moreBadge.className =
          "inline-flex items-center rounded-md bg-gray-100 px-2 py-1 mb-1 mr-2 text-xs font-medium text-gray-700";
        moreBadge.textContent = `+${i} more`;

        measurer.appendChild(moreBadge);
        const rect = moreBadge.getBoundingClientRect();
        moreTagMeasurements[i] = rect.width || 60;
        measurer.removeChild(moreBadge);
      }

      return { measurements, moreTagMeasurements };
    } catch (error) {
      console.warn("Badge measurement failed, using fallback layout:", error);
      // Fallback to simple logic
      const fallbackMeasurements = services?.map((service, index) => ({
        service,
        width: service.length * 7 + 25, // rough estimate
        index,
      }));
      const fallbackMoreMeasurements = {};
      for (let i = 1; i <= services.length; i++) {
        fallbackMoreMeasurements[i] = 50 + i.toString().length * 7;
      }
      return {
        measurements: fallbackMeasurements,
        moreTagMeasurements: fallbackMoreMeasurements,
      };
    } finally {
      if (document.body.contains(measurer)) {
        document.body.removeChild(measurer);
      }
    }
  };

  // Smart layout calculation with actual width measurement
  const calculateOptimalLayout = () => {
    if (predefinedServices.length <= 3) {
      // 3 or fewer services - show all
      return { visibleServices: predefinedServices, showMoreTag: false };
    }

    const { measurements, moreTagMeasurements } =
      measureBadgeWidths(predefinedServices);

    // Available width for vendor cards - conservative estimate
    const availableWidth = 280; // Adjusted for vendor card constraints
    const gapWidth = 4; // gap-1 = 4px between badges
    const lineHeight = 24; // Approximate height of badge + margin
    const maxLines = 2; // Maximum lines we want to allow

    // Try showing different numbers of services to find the optimal layout
    for (
      let showCount = predefinedServices.length;
      showCount >= 3;
      showCount--
    ) {
      const visibleServices = predefinedServices.slice(0, showCount);
      const remainingCount = predefinedServices.length - showCount;

      if (remainingCount === 0) {
        // Show all services - check if they fit
        const totalWidth = measurements
          .slice(0, showCount)
          .reduce((sum, m) => sum + m.width, 0);
        const totalGaps = (showCount - 1) * gapWidth;
        const totalNeeded = totalWidth + totalGaps;

        // Rough estimate: if total width is reasonable for 2 lines
        if (totalNeeded <= availableWidth * maxLines) {
          return { visibleServices, showMoreTag: false };
        }
      } else {
        // Check if showing these services + "+X more" fits better than showing more services
        const visibleWidth = measurements
          .slice(0, showCount)
          .reduce((sum, m) => sum + m.width, 0);
        const moreTagWidth = moreTagMeasurements[remainingCount] || 60;
        const totalGaps = showCount * gapWidth; // includes gap before "+X more"
        const totalWithMoreTag = visibleWidth + moreTagWidth + totalGaps;

        // Check if this layout fits reasonably
        if (totalWithMoreTag <= availableWidth * maxLines) {
          // Now check if showing the actual remaining services would fit better
          const allServicesWidth = measurements.reduce(
            (sum, m) => sum + m.width,
            0
          );
          const allServicesGaps = (predefinedServices.length - 1) * gapWidth;
          const allServicesTotal = allServicesWidth + allServicesGaps;

          // If all services would fit just as well, show them all
          if (allServicesTotal <= availableWidth * maxLines) {
            return { visibleServices: predefinedServices, showMoreTag: false };
          } else {
            // "+X more" layout is better
            return { visibleServices, showMoreTag: true };
          }
        }
      }
    }

    // Fallback: show 3 services + "+X more"
    return {
      visibleServices: predefinedServices.slice(0, 3),
      showMoreTag: true,
    };
  };

  const { visibleServices, showMoreTag } = !expanded
    ? calculateOptimalLayout()
    : { visibleServices: predefinedServices, showMoreTag: false };

  const remainingCount = predefinedServices.length - visibleServices.length;
  // Arrow should show if there are more than 3 total services, regardless of expanded state
  const isTruncated = predefinedServices.length > 3;

  return (
    <div
      data-provider-card
      className="relative overflow-hidden rounded-lg bg-white border border-gray-200 pt-0 shadow-sm"
    >
      {/* Top section: logo and menu */}
      <div className="relative flex items-center px-6 pb-5 pt-6 border-b border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
        {/* Selection circle icon - top left corner */}
        {canSelectVendors && (
          <button
            onClick={onToggleSelection}
            className={`absolute top-4 left-4 z-10 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 ${
              isSelected
                ? "bg-indigo-100 border-indigo-500 shadow-md ring-2 ring-indigo-200"
                : "bg-white border-slate-300 hover:bg-indigo-50 hover:border-indigo-300"
            }`}
            aria-label={isSelected ? "Deselect vendor" : "Select vendor"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              aria-hidden="true"
              className={`h-5 w-5 ${
                isSelected ? "text-indigo-700" : "text-slate-400"
              }`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </button>
        )}

        {/* Indigo selection overlay on top half of card */}
        {isSelected && (
          <div className="absolute inset-0 bg-indigo-100 opacity-30 pointer-events-none" />
        )}

        <div
          onClick={() => onProviderClick(provider)}
          className="flex-1 flex justify-center cursor-pointer"
        >
          <div className="rounded-md flex items-center justify-center h-20 w-48">
            {!brokenLogos[provider.name] &&
            // (providersWithLogos.includes(provider.name) || provider.logo) ? (
            provider.logo ? (
              <RotatingLogo
                logo={provider.logo}
                vendorName={provider.name}
                className={`max-h-16 w-auto ${
                  provider.name.toLowerCase().includes("908devices") ||
                  provider.name.toLowerCase().includes("10x")
                    ? "object-cover"
                    : "object-contain"
                }`}
                style={{
                  // minHeight: "3.5rem",
                  transform: "scale(1)",
                  // transform: (() => {
                  //   const name = provider.name.toLowerCase();
                  //   // Very small logos or those with excessive padding that need 2.5x scaling
                  //   if (
                  //     name.includes("908devices") ||
                  //     name.includes("10x genomics")
                  //   ) {
                  //     return "scale(2.5)";
                  //   }
                  //   // Small logos that need 2x scaling
                  //   if (
                  //     name === "3m" ||
                  //     name.includes("1doc") ||
                  //     name.includes("3dme") ||
                  //     name.includes("4g clinical") ||
                  //     name.includes("abl") ||
                  //     name.includes("abt")
                  //   ) {
                  //     return "scale(2.0)";
                  //   }
                  //   // Medium-small logos that need 1.5x scaling
                  //   if (
                  //     name.includes("aicure") ||
                  //     name.includes("veeva") ||
                  //     name.includes("ppd") ||
                  //     name.includes("abb") ||
                  //     name.includes("acm") ||
                  //     name.includes("aes") ||
                  //     name.includes("agility") ||
                  //     name.includes("aixial")
                  //   ) {
                  //     return "scale(1.5)";
                  //   }
                  //   // Large logos that are already big enough - no scaling
                  //   if (
                  //     name.includes("100plus") ||
                  //     name.includes("iqvia") ||
                  //     name.includes("icon") ||
                  //     name.includes("parexel") ||
                  //     name.includes("covance") ||
                  //     name.includes("syneos") ||
                  //     (name.includes("clinical") &&
                  //       !name.includes("4g clinical")) ||
                  //     name.includes("labcorp") ||
                  //     name.includes("medpace") ||
                  //     name.includes("oracle") ||
                  //     name.includes("premier") ||
                  //     name.includes("quintiles")
                  //   ) {
                  //     return "scale(1)";
                  //   }
                  //   // Default: slight scaling for medium logos
                  //   return "scale(1.2)";
                  // })(),
                  transformOrigin: "center",
                }}
                providerLogoUrl={provider.logo}
                onExhausted={() => onImgError(provider.name)}
              />
            ) : (
              <span className="h-16 w-auto flex items-center justify-center text-4xl font-normal text-gray-400 text-center hover:text-gray-600 transition-colors duration-200 opacity-75">
                <ProviderName name={provider.name} />
            </span>
            )}
          </div>
        </div>
        <div className="absolute top-4 right-6 z-10">
          <ActionMenu
            provider={provider}
            urlLink={"/provider/overview"}
            onActionClick={() => onProviderClick(provider)}
            hideRequestActions={hideRequestActions}
            canManageMyVendors={canManageMyVendors}
            myVendorMembership={
              getMyVendorMembership ? getMyVendorMembership(provider.id) : null
            }
            onAddToMyVendors={onAddToMyVendors}
            onRemoveFromMyVendors={onRemoveFromMyVendors}
            onAddToHidden={onAddToHidden}
            onUnhide={onUnhide}
            onCreateList={onCreateList}
            onAddToList={onAddToList}
            isBookmarked={providerBookmarked}
            isBookmarkPending={providerBookmarkPending}
            onToggleBookmark={onToggleBookmark}
          />
        </div>
      </div>

      {/* Info rows: Location, Type, GxP, Services, Documents */}
      <div className="divide-y divide-gray-100 px-6 py-2">
        <div className="flex justify-between items-center py-3">
          <span className="text-sm text-gray-500">Primary Location</span>
          <span className="text-sm font-normal pr-2">
            {resolvePrimaryLocationDisplay(provider, provider?.locations || []) || (
              <span className="text-gray-400">–</span>
            )}
          </span>
        </div>

        <div className="flex justify-between items-center py-3">
          <span className="text-sm text-gray-500">Type</span>
          <span className="text-sm font-normal pr-2 flex items-center gap-2">
            {provider.provider_type &&
              vendorTypeColors[provider.provider_type] && (
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    vendorTypeColors[provider.provider_type]
                  }`}
                />
              )}
            {provider.provider_type === "Service/Technology Vendor"
              ? "Service/Technology Provider"
              : provider.provider_type || "Service/Technology Provider"}
          </span>
        </div>

        {/* TODO: gxp card row */}
        {/* GxP field - same line layout as Type */}
        {/* {uniqueGxPs.length > 0 && (
          <div className="flex justify-between items-center py-3">
            <span className="text-sm text-gray-500">GxP</span>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              {uniqueGxPs?.map((gxp, index) => (
                <span
                  key={`gxp-${index}`}
                  className="inline-flex items-center rounded-md bg-slate-500 px-2 py-1 text-xs font-medium text-white"
                >
                  {gxp}
                </span>
              ))}
            </div>
          </div>
        )} */}

        {/* Services with Expandable Functionality */}
        <div className="flex flex-col py-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-500">Services</span>
            {/* Dropdown Arrow */}
            {isTruncated && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none p-1"
                aria-label={expanded ? "Collapse services" : "Expand services"}
              >
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${
                    expanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="relative">
            <div
              className={`flex flex-wrap gap-1 transition-all duration-200 ease-in-out ${
                expanded ? "max-h-full" : "max-h-[4rem] overflow-hidden"
              }`}
            >
              {/* Show visible services */}
              {(expanded ? predefinedServices : visibleServices)?.map((s) => {
                const { displayName, color } = resolveServiceBadgePresentation(
                  s,
                  serviceColors
                );
                const colorStyle = color
                  ? {
                      backgroundColor: color?.bgColor,
                      color: color.textColor,
                      boxShadow: `inset 0 0 0 1px ${color.borderColor}1A`,
                    }
                  : {};
                return (
                  <span
                    key={s}
                    style={colorStyle}
                    className={`inline-flex items-center rounded px-2 py-1 mb-1 mr-2 text-xs font-medium ${color?.textColor} ring-1 ring-inset ${color?.borderColor}`}
                    title={displayName}
                  >
                    {displayName}
                    <ServiceCategoryTooltip name={displayName} />
                  </span>
                );
              })}

              {/* Show "+X more" badge */}
              {showMoreTag && (
                <span
                  className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 mb-1 mr-2 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                >
                  +{remainingCount} more
                </span>
              )}
            </div>

            {/* Soft fade-out gradient overlay */}
            {!expanded && isTruncated && (
              <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center py-3">
          <span className="text-sm text-gray-500">Documents</span>
          <span className="text-sm font-normal pr-2">
            <div className="flex items-center gap-3">
              {!hideVqq && (
                <div className="flex items-center">
                  <CheckCircleIcon className="w-[20px] h-[20px] text-indigo-700" />
                  <span className="inline-block pl-2">
                    {occurrences(provider, "RFI")} VQQs
                  </span>
                </div>
              )}
              <div className="flex items-center">
                <CheckBadgeIcon className="w-[20px] h-[20px] text-rose-700" />
                <span className="inline-block pl-2">
                  {occurrences(provider, "Audit")} Audits
                </span>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>
  );
}

// Internal logo component that tries multiple src candidates sequentially
function RotatingLogo({
  vendorName,
  className,
  style,
  providerLogoUrl,
  onExhausted,
  logo,
}) {
  const [candidates, setCandidates] = React.useState([]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      // const manifestPath = await getLocalLogoPath(vendorName);
      const manifestPath = logo;
      const localList = [manifestPath, ...getLogoSrcCandidates(vendorName)];
      const list = providerLogoUrl
        ? [...localList, providerLogoUrl]
        : localList;
      if (mounted) setCandidates(list);
    })();
    return () => {
      mounted = false;
    };
  }, [vendorName, providerLogoUrl]);
  const [idx, setIdx] = React.useState(0);
  const [failed, setFailed] = React.useState(false);

  const handleError = () => {
    if (idx < candidates.length - 1) {
      setIdx(idx + 1);
    } else {
      setFailed(true);
      onExhausted && onExhausted();
    }
  };

  if (failed) return null;

  return (
    <img
      src={candidates[idx]}
      alt={`${vendorName} logo`}
      className={className}
      style={style}
      onError={handleError}
      loading="lazy"
    />
  );
}
